import { Chart, Line } from '@mm/components';
import {
  SEMRushWebsiteTrafficByMonthModel,
  SEMRushWebsiteTrafficModel,
} from '@mm/state/model/SEMRushWebsiteTrafficModel';
import React from 'react';
import { PrimaryButton } from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import styles from './MissedOpportunities.module.css';
import { SafetyCertificateFilled } from '@ant-design/icons';
import { LevelMeter } from '../LevelMeter/LevelMeter';
import {
  AVERAGE_WEB_TRAFFIC_GROWTH,
  AVERAGE_WEB_CONVERSION_RATE,
  SPLIT_NUMBER_BY_THOUTHANDS,
  MAX_LOST_LEADS_THRESHOLD,
  beautifyNumber,
  WEB_TRAFFIC_GROWTH_WITH_MOBILEMONKEY,
  REDUCE_COST_PER_LEAD,
} from '../WebsiteReportSummary/constants';

type Props = {
  website: string;
  isOrganic: boolean;
  traffic: SEMRushWebsiteTrafficModel;
  trafficByMonth: SEMRushWebsiteTrafficByMonthModel;
  onClick: () => void;
};

// tslint:disable-next-line: cyclomatic-complexity
export const MissedOpportunities = (props: Props) => {
  const { website, isOrganic, traffic, trafficByMonth, onClick } = props;
  const organicTraffic = traffic.trafficTypes?.organic || 0;
  const monthlyOrganicTraffic = trafficByMonth.trafficTypes?.organic;
  const monthlyPaidTraffic = trafficByMonth.trafficTypes?.paid;
  const paidTraffic = traffic.trafficTypes?.paid || 0;
  const lostOrganicLeads = organicTraffic * AVERAGE_WEB_TRAFFIC_GROWTH;
  const lostPaidTraffic = paidTraffic * AVERAGE_WEB_TRAFFIC_GROWTH;
  const currentConvertedLeads = organicTraffic * AVERAGE_WEB_CONVERSION_RATE;
  const extraLeads = paidTraffic * AVERAGE_WEB_TRAFFIC_GROWTH;

  const missedOpportunities = isOrganic ? lostOrganicLeads : lostPaidTraffic;

  const trafficDataByMonth = trafficByMonth.trafficTypes && {
    labels: trafficByMonth.trafficTypes?.dates || [],
    data: [isOrganic ? monthlyOrganicTraffic || [] : monthlyPaidTraffic || []],
    color: [isOrganic ? '#40A9FF' : '#FFC53D'],
  };

  const AVERAGE_COST_PER_LEAD = 50.17;
  const AVERAGE_COST_PER_CLICK = 2;

  const tooltipsCallbacks = {
    label: (
      item: { datasetIndex: number; value: number },
      { datasets }: { datasets: { label: string }[] },
    ) => {
      const { label } = datasets[0];
      const value = item.value
        .toString()
        .replace(SPLIT_NUMBER_BY_THOUTHANDS, ',');

      return `${label}: ${value} visitors`;
    },
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <div className={styles.note}>
            {isOrganic ? 'part a. ' : 'part b. '}
            <span className={styles.website}>{website}</span>
          </div>
          {isOrganic
            ? 'Missing leads from organic traffic'
            : 'Paid search lead capture missed opportunities'}
        </div>
        <LevelMeter
          maxThresholdValue={MAX_LOST_LEADS_THRESHOLD}
          currentValue={missedOpportunities}
        />
      </div>
      {(isOrganic && monthlyOrganicTraffic) || monthlyPaidTraffic ? (
        <div className={styles.chartWrapper}>
          <Chart
            className={styles.chart}
            name={isOrganic ? 'Organic Traffic' : 'Paid Traffic'}
            data={trafficDataByMonth}
            tooltipsCallbacks={tooltipsCallbacks}
          />
        </div>
      ) : null}
      <div className={styles.content}>
        <div className={styles.barGraph}>
          <span className={styles.graphTitle}>
            {' '}
            {isOrganic
              ? 'Unique visitor emails captured per month'
              : 'Leads per $100 of media spend'}
          </span>

          <div className={styles.barsWrapper}>
            <div className={styles.bar}>
              <span className={styles.number}>
                {isOrganic ? beautifyNumber(currentConvertedLeads) : '2'}*
              </span>
              <span className={styles.smallBar} />
              <span>{isOrganic ? 'Currently' : 'Before'}</span>
            </div>
            <div className={styles.bar}>
              <span className={styles.number}>
                {isOrganic
                  ? beautifyNumber(
                      organicTraffic * WEB_TRAFFIC_GROWTH_WITH_MOBILEMONKEY,
                    )
                  : beautifyNumber(
                      paidTraffic * WEB_TRAFFIC_GROWTH_WITH_MOBILEMONKEY,
                    )}
              </span>
              <span className={styles.bigBar} />
              {isOrganic && (
                <div className={styles.more}>
                  7.5x <span className={styles.description}>More</span>
                </div>
              )}
              <span>{isOrganic ? 'With MobileMonkey' : 'After'}</span>
            </div>
            <Line className={styles.graphLine} />
          </div>
        </div>
        <div className={styles.heading}>
          {isOrganic ? (
            <div className={styles.raggedText}>
              You are missing out on capturing {''}
              {beautifyNumber(organicTraffic * AVERAGE_WEB_TRAFFIC_GROWTH)}{' '}
              leads per month from your organic traffic
            </div>
          ) : (
            'Paid Lead Capture Report: Room for Improvement!'
          )}
          <div className={styles.description}>
            {!isOrganic && (
              <ul>
                <li>
                  We estimate your paid traffic to be{' '}
                  {beautifyNumber(paidTraffic)} visitors / mo.
                </li>
                <div className={styles.raggedText}>
                  <li>
                    At an average cost per click of ${AVERAGE_COST_PER_CLICK}{' '}
                    this results in a monthly ad spend of approximately $
                    {beautifyNumber(paidTraffic * AVERAGE_COST_PER_CLICK)}.{' '}
                  </li>
                </div>

                <li>
                  *Using an industry average conversion rate of{' '}
                  {AVERAGE_WEB_CONVERSION_RATE * 100}% this results in roughly{' '}
                  {beautifyNumber(paidTraffic * AVERAGE_WEB_CONVERSION_RATE)}{' '}
                  leads per month from ads, for an average cost per lead of $
                  {AVERAGE_COST_PER_LEAD}.
                </li>
              </ul>
            )}

            {isOrganic ? (
              `*Based on your estimated ${beautifyNumber(
                organicTraffic,
              )} visitors per month and the industry average conversion rate of approximately 2% we estimate you are failing to 
                capture ${beautifyNumber(
                  lostOrganicLeads,
                )} leads per month that MobileMonkey could be acquiring for you. `
            ) : (
              <div className={styles.descriptionBg}>
                MobileMonkey could convert an additional{' '}
                {beautifyNumber(extraLeads)} leads which would have cost an{' '}
                <span className={styles.bold700}>
                  additional $
                  {beautifyNumber(extraLeads * AVERAGE_COST_PER_LEAD)}
                </span>{' '}
                using traditional paid media channels.{' '}
              </div>
            )}
          </div>

          <div className={styles.action}>
            <PrimaryButton className={styles.button} onClick={onClick}>
              {isOrganic
                ? `Capture ${beautifyNumber(lostOrganicLeads)} lost leads / mo`
                : 'Get Started'}
            </PrimaryButton>
            {!isOrganic && (
              <div className={styles.descriptionBg}>
                <SafetyCertificateFilled className={styles.safetyCertificate} />
                Decrease your cost of acquisition by $
                {AVERAGE_COST_PER_LEAD - REDUCE_COST_PER_LEAD} per lead*
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
